import {
	hideLoadingScreen,
	modal,
	router,
	setPageContent,
	showLoadingScreen,
} from "..";
import {
	delFarmcamSim,
	postFarmcamSimData,
	putFarmcamSim,
} from "../api/farmcam-sims_{imsi}";
import { getPrices } from "../api/prices";
import {
	nameField,
	ownerField,
	sharingField,
	subscriptionField,
	tagsField,
} from "../device/device_settings_fields";
import { openUnregisterDeviceModal } from "../device/unregister_device_modal";
import { locale } from "../locale";
import { state } from "../state";
import { formatDataAmount } from "../utils";
import { Bulma } from "../view/bulma";
import { centeredBox, errorToast } from "../view/components";
import { Html } from "../view/html";

/**
 * @type {import('navigo').Handler}
 */
async function loadFarmcamSimSettingsPage(ctx) {
	if (!ctx || !ctx.data || !ctx.data.imsi) throw "missing ctx.data.imsi";
	const fcs = state.farmcamSims[ctx.data.imsi];

	const user = await state.getActiveUser();
	const currency = user.Country === "SE" ? "SEK" : "EUR";
	const isOwner = user.Email === fcs.Metadata.OwnerEmail;

	const {
		FarmcamSimSmall,
		FarmcamSimMedium,
		FarmcamSimLarge,
		FarmcamSimExtraData,
	} = await getPrices();

	const subscription = subscriptionField(
		fcs.Subscription,
		[FarmcamSimSmall, FarmcamSimMedium, FarmcamSimLarge],
		currency,
		isOwner,
	);
	const sharing = sharingField(fcs.Metadata);
	const tags = tagsField(fcs.Metadata);
	const name = nameField(fcs.Metadata.Name);
	const owner = ownerField(fcs.Metadata, isOwner);

	const save = () => {
		putFarmcamSim(fcs.Imsi, {
			PriceId: subscription.getPriceId(),
			Name: name.getValue(),
			Access: sharing.getAccess(),
			Notify: sharing.getNotify(),
			Tags: tags.getValue(),
			Owner: owner.getValue(),
		})
			.then((fcs) => {
				state.farmcamSims[fcs.Imsi] = fcs;
				router.jump(`/devices?id=${fcs.Imsi}`);
			})
			.catch(errorToast)
			.finally(hideLoadingScreen);
	};

	const unregister = () => {
		showLoadingScreen();
		delFarmcamSim(fcs.Imsi)
			.then(() => {
				delete state.farmcamSims[fcs.Imsi];
				router.jump("/devices");
			})
			.catch(errorToast)
			.finally(hideLoadingScreen);
	};

	setPageContent(
		centeredBox(
			"small",
			Bulma.title(`FarmCam SIM ${fcs.Metadata.Name}`, "is-4"),
			Bulma.subtitle(`IMSI: ${fcs.Imsi}`, "is-6"),
			buyExtraDataField(ctx, fcs, FarmcamSimExtraData),
			subscription.element,
			sharing.element,
			tags.element,
			name.element,
			owner.element,
			Bulma.buttons(
				Bulma.button(
					{
						onClick: save,
						styles: ["is-primary"],
					},
					locale.strings.save,
				),
				Bulma.button(
					{
						styles: ["is-primary"],
						onClick: () => router.jump(`/devices?id=${fcs.Imsi}`),
					},
					locale.strings.back,
				),
				Bulma.button(
					{
						disabled: !isOwner,
						onClick: () =>
							openUnregisterDeviceModal(
								"FarmCam SIM",
								unregister,
							),
						styles: ["is-danger"],
					},
					locale.strings.unregister,
				),
			),
		),
	);
}

/**
 * @param {import('navigo').Match} ctx
 * @param {FarmcamSim} fcs
 * @param {import('../api/prices').Price} price
 */
function buyExtraDataField(ctx, fcs, price) {
	const root = Bulma.field(
		{},
		Bulma.label(
			locale.string({
				enGB: `Remaining data: ${formatDataAmount(fcs.RemainingData)}`,
				deDE: `Verbleibende Daten: ${formatDataAmount(fcs.RemainingData)}`,
				frFR: `Données restantes : ${formatDataAmount(fcs.RemainingData)}`,
				svSE: `Återstående data: ${formatDataAmount(fcs.RemainingData)}`,
			}),
		),
		Bulma.control(
			{},
			Bulma.button(
				{
					disabled: !fcs.Subscription.PriceIds.Current,
					onClick: onClickBuyExtraData,
					styles: ["is-primary"],
				},
				locale.string({
					enGB: "Buy extra data",
					deDE: "Zusätzliche Daten kaufen",
					frFR: "Acheter des données supplémentaires",
					svSE: "Köp extra data",
				}),
			),
		),
	);

	async function onClickBuyExtraData() {
		modal.open({
			header: locale.string({
				enGB: "Buy extra data",
				deDE: "Zusätzliche Daten kaufen",
				frFR: "Acheter des données supplémentaires",
				svSE: "Köp extra data",
			}),
			body: [
				Html.paragraph(
					locale.string({
						enGB: `Confirm your purchase of 1 GB additional data for ${await price.toString()} ex VAT`,
						deDE: `Bestätigen Sie Ihren Kauf von 1 GB zusätzlichen Daten für ${await price.toString()} ohne MwSt.`,
						frFR: `Confirmez votre achat de 1 Go de données supplémentaires pour ${await price.toString()} HTVA`,
						svSE: `Bekräfta ditt köp av 1 GB extra data för ${await price.toString()} ex moms`,
					}),
				),
			],
			footer: [
				Bulma.button(
					{
						onClick: onClickConfirm,
						styles: ["is-primary"],
					},
					locale.strings.confirm,
				),
			],
		});
	}

	async function onClickConfirm() {
		showLoadingScreen();
		postFarmcamSimData(fcs.Imsi)
			.then(() => loadFarmcamSimSettingsPage(ctx))
			.catch(errorToast)
			.finally(hideLoadingScreen);
	}

	return root;
}

export { loadFarmcamSimSettingsPage };
